import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TextMaskService } from '@bayclubs/ui-text-mask';

const noop = () => { };
export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimePickerComponent),
    multi: true
};

@Component({
    selector: 'app-time-picker',
    templateUrl: './time-picker.component.html',
    styleUrls: ['./time-picker.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
    standalone: false
})
export class TimePickerComponent implements OnInit, ControlValueAccessor {
    @Input() pickerWidth = 150;

    @Input()
    public readonly: boolean;

    @Input()
    public invalid: boolean;

    public timeInner = 0;

    private options = { step: 5 };

    // Placeholders for the callbacks which are later providesd
    // by the Control Value Accessor
    private onTouchedCallback: (_: any) => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    constructor(public textMaskService: TextMaskService) {
    }

    ngOnInit() { }

    // From ControlValueAccessor interface
    writeValue(value: any) {
        this.timeInner = value;
        if (value !== this.value) {
            this.value = value;
        }
    }

    get value(): any {
        return this.timeInner;
    }

    // set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.timeInner) {
            this.setTime(v);
        }
    }

    // From ControlValueAccessor interface
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    // From ControlValueAccessor interface
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.readonly = isDisabled;
    }

    public isAm(): boolean {
        return this.timeInner < 720;
    }

    public isPm(): boolean {
        return !this.isAm();
    }

    public onHourUp(): void {
        this.setTime(this.timeInner + 60);
    }

    public onHourDown(): void {
        this.setTime(this.timeInner - 60);
    }

    public onMinuteUp(): void {
        this.setTime(this.timeInner + this.options.step);
    }

    public onMinuteDown(): void {
        this.setTime(this.timeInner - this.options.step);
    }

    public onPm(): void {
        if (this.isAm()) {
            this.setTime(this.timeInner + 720);
        }
    }

    public onAm(): void {
        if (this.isPm()) {
            this.setTime(this.timeInner - 720);
        }
    }

    public menuClicked($event): boolean {
        $event.stopPropagation();
        return false;
    }

    public minutesManualChanged($event) {
        const value = parseInt($event.srcElement.value, 10);

        let newTime = this.getHour() * 60;
        if (value >= 0 && value <= 59) {
            newTime += value;
        }

        this.setTime(newTime);
    }

    public getHour(): number {
        return Math.floor(this.timeInner / 60);
    }

    public getHourIn12Format(): number {
        const hours = this.getHour();
        if (hours > 12) {
            return hours - 12;
        }
        if (hours === 0) {
            return 12;
        }

        return hours;
    }

    public getMinutes() {
        return this.timeInner - this.getHour() * 60;
    }

    public getAmPm(): string {
        return this.isAm() ? 'AM' : 'PM';
    }

    public get canIncrement() {
        return this.timeInner < 1439;
    }

    public get canDecrement() {
        return this.timeInner > 0;
    }

    private setTime(newTime: number) {
        if (newTime < 0) {
            newTime = 0;
        }

        if (newTime > 1439) {
            newTime = 1439;
        }

        this.timeInner = newTime;
        this.onChangeCallback(this.timeInner);
    }
}
